if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form.addEventListener('formdata', ({ formData }) => {
          // Keep deferred placeholders for order handling, without displaying them in checkout.
          for (const name of [
            'Device Manufacturer', 'Device Name', 'Hearing Aid Brand',
            'Hearing Aid Model', 'CI Brand', 'CI Model'
          ]) {
            const key = `properties[${name}]`;
            if (formData.get(key) !== 'To be provided after order') continue;
            formData.set(`properties[_${name}]`, formData.get(key));
            formData.delete(key);
          }

          // Keep one pair of visible properties on checkout. Older forms may
          // submit the generic manufacturer/name aliases alongside the true
          // hearing-aid or cochlear brand and model.
          const readProperty = name => formData.get('properties[' + name + ']');
          const writeProperty = (name, value) => formData.set('properties[' + name + ']', value);
          const genericBrand = readProperty('Device Manufacturer');
          const genericModel = readProperty('Device Name');
          const isCochlear = Boolean(
            readProperty('CI Brand') || readProperty('CI Model') ||
            this.closest('product-info')?.querySelector('[data-auzi-cochlear-device]') ||
            ['Cochlear', 'MED-EL', 'Advanced Bionics'].includes(genericBrand)
          );
          const brandName = isCochlear ? 'CI Brand' : 'Hearing Aid Brand';
          const modelName = isCochlear ? 'CI Model' : 'Hearing Aid Model';
          const empty = value => !value || value === 'To be provided after order';

          if (!empty(genericBrand) && empty(readProperty(brandName))) {
            writeProperty(brandName, genericBrand);
          }
          if (!empty(genericModel) && empty(readProperty(modelName))) {
            writeProperty(modelName, genericModel);
          }
          formData.delete('properties[Device Manufacturer]');
          formData.delete('properties[Device Name]');

          const confirmation = readProperty('Device confirmation');
          if (confirmation !== null) {
            if (!readProperty('_Device confirmation')) {
              writeProperty('_Device confirmation', confirmation);
            }
            formData.delete('properties[Device confirmation]');
          }
        });
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        if (evt.defaultPrevented) return;

        const hasFileProperty = Array.from(
          this.form.querySelectorAll('input[type="file"][name^="properties["]')
        ).some((input) => input.files && input.files.length > 0);

        // A file line-item property must be submitted as the browser's native
        // multipart product form. Do not intercept it with the AJAX cart code.
        if (hasFileProperty) {
          this.form.enctype = 'multipart/form-data';
          return;
        }

        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const productInfo = this.closest('product-info');
        const variantSelectors = productInfo?.querySelector('variant-selects');
        const variantModelNode = productInfo?.querySelector('[data-auzi-variants]');

        if (variantSelectors && variantModelNode) {
          try {
            const variants = JSON.parse(variantModelNode.textContent);
            const selectedValues = getProductOptionControls(variantSelectors).map(
              (select) => select.value
            );
            const selectedVariant = Array.isArray(variants)
              ? variants.find(
                  (variant) =>
                    variant.options?.length === selectedValues.length &&
                    variant.options.every((value, index) => value === selectedValues[index])
                )
              : null;

            if (!selectedVariant?.id) return;

            this.variantIdInput.value = selectedVariant.id;
            this.variantIdInput.disabled = false;

            const selectedVariantNode = variantSelectors.querySelector('[data-selected-variant]');
            if (selectedVariantNode) selectedVariantNode.textContent = JSON.stringify(selectedVariant);
          } catch (error) {
            console.error('Unable to reconcile selected product variant before add to cart.', error);
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // Hearing aid covers and HEARLOOM: carry the precise photographed shell to the cart.
        // An uploaded device photo remains the preferred image on the cart line.
        const hearingChoice = this.closest('product-info')?.querySelector('[data-auzi-cover-device]');
        if (hearingChoice?.classList.contains('auzi-specific-device-chosen')) {
          const exactImage = hearingChoice.querySelector('[data-auzi-specific-selected-image]')?.getAttribute('src');
          if (exactImage) formData.set('properties[_auzi_device_image]', exactImage);
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
